import { initializeApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import {
  getDatabase,
  ref,
  set,
  push,
  onValue,
  get,
  query,
  orderByChild,
  equalTo,
  update,
  remove,
  DataSnapshot,
} from "firebase/database";
import { getFirestore } from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  updateMetadata,
} from "firebase/storage";

// Firebase 구성 - 환경 변수로 관리
const firebaseConfig = {
  apiKey:
    process.env.REACT_APP_FIREBASE_API_KEY ||
    "AIzaSyD5PXmftN7I_XuV92st4OddtjX22qVaepY",
  authDomain:
    process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "pet-milly.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "pet-milly",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "pet-milly",
  messagingSenderId:
    process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "900095894245",
  appId:
    process.env.REACT_APP_FIREBASE_APP_ID ||
    "1:900095894245:web:3313ae08cae5afba242a0c",
  measurementId:
    process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-VY81ZT0FLD",
};

// Firebase 초기화
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const database = getDatabase(app);
const db = getFirestore(app);

// Storage 초기화 (개선된 설정)
const storage = getStorage(app);

// Storage 설정 최적화 (CORS 문제 해결)
// Firebase SDK v9는 자동으로 CORS를 처리하지만 추가 설정으로 안정성 향상
// Resumable Upload를 기본으로 사용하여 CORS 문제 우회

// Storage 연결 테스트 함수
export const testStorageConnection = async (): Promise<boolean> => {
  try {
    // Storage 연결 테스트 (앱 정보 확인)
    console.log("Storage 앱 이름:", storage.app.name);
    console.log("Storage 버킷 (한국 리전):", storage.app.options.storageBucket);

    // 현재 사용자 확인
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.warn("Storage 테스트: 사용자가 로그인되어 있지 않음");
      return false;
    }

    console.log("Storage 테스트 - 현재 사용자:", {
      uid: currentUser.uid,
      email: currentUser.email,
    });

    // 실제 Storage 버킷에 접근 테스트
    const testRef = storageRef(storage, "test-connection");
    console.log("Storage 참조 생성 성공 (한국 리전):", testRef.bucket);

    // 권한 테스트를 위한 간단한 업로드 시도
    try {
      const testBlob = new Blob(["test"], { type: "text/plain" });
      const testFile = new File([testBlob], "test.txt", { type: "text/plain" });

      const testUploadRef = storageRef(
        storage,
        `test-connection-${Date.now()}.txt`
      );
      await uploadBytes(testUploadRef, testFile);

      console.log("Storage 권한 테스트 성공 (한국 리전)");

      // 테스트 파일 정리는 하지 않음 (권한 확인 목적)
      return true;
    } catch (uploadError: any) {
      console.warn(
        "Storage 업로드 테스트 실패:",
        uploadError.code || uploadError.message
      );
      // 업로드 실패해도 연결은 성공으로 간주
      return true;
    }
  } catch (error) {
    console.error("Storage 연결 테스트 실패:", error);
    return false;
  }
};

// 현재 인증 상태 확인 함수
export const checkAuthState = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// 채팅 관련 함수들
// 새로운 채팅방 생성
export const createChatRoom = async (user1Id: string, user2Id: string) => {
  // 두 사용자 ID를 정렬하여 일관된 채팅방 ID 생성
  const users = [user1Id, user2Id].sort();
  const chatRoomId = `${users[0]}_${users[1]}`;

  const chatRoomRef = ref(database, `chatRooms/${chatRoomId}`);

  // 채팅방 정보 저장
  await set(chatRoomRef, {
    participants: {
      [user1Id]: true,
      [user2Id]: true,
    },
    createdAt: new Date().toISOString(),
  });

  // 사용자의 채팅방 목록에 추가
  await set(ref(database, `userChatRooms/${user1Id}/${chatRoomId}`), true);
  await set(ref(database, `userChatRooms/${user2Id}/${chatRoomId}`), true);

  return chatRoomId;
};

// 메시지 전송
export const sendMessage = async (
  chatRoomId: string,
  userId: string,
  message: string
) => {
  const messagesRef = ref(database, `messages/${chatRoomId}`);
  const newMessageRef = push(messagesRef);

  await set(newMessageRef, {
    senderId: userId,
    text: message,
    timestamp: new Date().toISOString(),
    read: false, // 초기에는 읽지 않은 상태로 설정
  });

  // 채팅방의 마지막 메시지 정보 업데이트
  await set(ref(database, `chatRooms/${chatRoomId}/lastMessage`), {
    text: message,
    senderId: userId,
    timestamp: new Date().toISOString(),
    read: false,
  });
};

// 메시지를 읽음 상태로 표시
export const markMessagesAsRead = async (
  chatRoomId: string,
  messageIds: string[]
) => {
  if (!messageIds.length) return;

  const updates: { [key: string]: any } = {};

  // 각 메시지의 읽음 상태 업데이트
  messageIds.forEach((messageId) => {
    updates[`messages/${chatRoomId}/${messageId}/read`] = true;
  });

  // 마지막 메시지가 읽음 상태가 될 경우 채팅방의 lastMessage도 업데이트
  const messagesRef = ref(database, `messages/${chatRoomId}`);
  const snapshot = await get(messagesRef);
  const messages: any[] = [];

  snapshot.forEach((childSnapshot) => {
    messages.push({
      id: childSnapshot.key,
      ...childSnapshot.val(),
    });
  });

  // 시간순으로 정렬
  messages.sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  // 마지막 메시지의 ID가 읽음 처리될 메시지 중 하나인지 확인
  if (messages.length > 0) {
    const lastMessageId = messages[messages.length - 1].id;
    if (messageIds.includes(lastMessageId)) {
      updates[`chatRooms/${chatRoomId}/lastMessage/read`] = true;
    }
  }

  // 일괄 업데이트 실행
  return update(ref(database), updates);
};

// 채팅방의 메시지 불러오기
export const listenToMessages = (
  chatRoomId: string,
  callback: (messages: any[]) => void
) => {
  const messagesRef = ref(database, `messages/${chatRoomId}`);

  return onValue(messagesRef, (snapshot) => {
    const messages: any[] = [];
    snapshot.forEach((childSnapshot) => {
      const message = childSnapshot.val();
      messages.push({
        id: childSnapshot.key,
        text: message.text,
        senderId: message.senderId,
        timestamp: message.timestamp,
        read: message.read !== undefined ? message.read : true, // 읽음 상태가 없는 기존 메시지는 읽음으로 처리
      });
    });

    // 시간순으로 정렬
    messages.sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    callback(messages);
  });
};

// 사용자의 채팅방 목록 가져오기
export const getUserChatRooms = async (userId: string) => {
  const userChatRoomsRef = ref(database, `userChatRooms/${userId}`);
  const snapshot = await get(userChatRoomsRef);

  if (!snapshot.exists()) {
    return [];
  }

  const chatRooms: any[] = [];
  const promises = Object.keys(snapshot.val()).map(async (chatRoomId) => {
    const chatRoomRef = ref(database, `chatRooms/${chatRoomId}`);
    const chatRoomSnapshot = await get(chatRoomRef);

    if (chatRoomSnapshot.exists()) {
      const chatRoomData = chatRoomSnapshot.val();

      // 상대방 ID 찾기
      const participants = Object.keys(chatRoomData.participants);
      const otherUserId = participants.find((id) => id !== userId);

      // 채팅방 정보 추가
      chatRooms.push({
        id: chatRoomId,
        otherUserId,
        lastMessage: chatRoomData.lastMessage || null,
        createdAt: chatRoomData.createdAt,
        read: chatRoomData.lastMessage
          ? chatRoomData.lastMessage.senderId === userId ||
            chatRoomData.lastMessage.read
          : true,
      });
    }
  });

  await Promise.all(promises);

  // 마지막 메시지 시간순으로 정렬
  return chatRooms.sort((a, b) => {
    if (!a.lastMessage) return 1;
    if (!b.lastMessage) return -1;
    return (
      new Date(b.lastMessage.timestamp).getTime() -
      new Date(a.lastMessage.timestamp).getTime()
    );
  });
};

// 사용자의 채팅방 목록을 실시간으로 감시
export const listenToChatRooms = (
  userId: string,
  callback: (chatRooms: any[]) => void
) => {
  // 사용자의 채팅방 목록에 대한 참조
  const userChatRoomsRef = ref(database, `userChatRooms/${userId}`);

  // 채팅방 데이터를 저장할 객체
  const chatRoomsData: { [key: string]: any } = {};

  // 채팅방별 리스너를 저장할 객체
  const chatRoomListeners: { [key: string]: () => void } = {};

  // 채팅방 목록 변경 감지 리스너
  const userRoomsListener = onValue(userChatRoomsRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback([]);
      return;
    }

    const chatRoomIds = Object.keys(snapshot.val());

    // 더 이상 존재하지 않는 채팅방의 리스너 제거
    Object.keys(chatRoomListeners).forEach((roomId) => {
      if (!chatRoomIds.includes(roomId)) {
        chatRoomListeners[roomId](); // 리스너 해제
        delete chatRoomListeners[roomId];
        delete chatRoomsData[roomId];
      }
    });

    // 각 채팅방에 대한 리스너 설정
    chatRoomIds.forEach((roomId) => {
      if (!chatRoomListeners[roomId]) {
        const chatRoomRef = ref(database, `chatRooms/${roomId}`);

        // 채팅방 정보에 대한 리스너 설정
        const chatRoomListener = onValue(chatRoomRef, (chatRoomSnapshot) => {
          if (chatRoomSnapshot.exists()) {
            const chatRoomData = chatRoomSnapshot.val();

            // 상대방 ID 찾기
            const participants = Object.keys(chatRoomData.participants);
            const otherUserId = participants.find((id) => id !== userId) || "";

            // 읽음 상태 확인 (내가 보낸 메시지이거나 읽은 메시지면 true)
            const isRead =
              !chatRoomData.lastMessage ||
              chatRoomData.lastMessage.senderId === userId ||
              chatRoomData.lastMessage.read === true;

            // 채팅방 정보 업데이트
            chatRoomsData[roomId] = {
              id: roomId,
              otherUserId,
              lastMessage: chatRoomData.lastMessage || null,
              createdAt: chatRoomData.createdAt,
              read: isRead,
            };

            // 최신 정렬된 채팅방 목록을 콜백으로 전달
            const sortedRooms = Object.values(chatRoomsData).sort(
              (a: any, b: any) => {
                if (!a.lastMessage) return 1;
                if (!b.lastMessage) return -1;
                return (
                  new Date(b.lastMessage.timestamp).getTime() -
                  new Date(a.lastMessage.timestamp).getTime()
                );
              }
            );

            callback(sortedRooms);
          }
        });

        // 리스너 저장
        chatRoomListeners[roomId] = chatRoomListener;
      }
    });
  });

  // 모든 리스너를 해제하는 함수 반환
  return () => {
    userRoomsListener();
    Object.values(chatRoomListeners).forEach((unsubscribe) => unsubscribe());
  };
};

// 채팅방 삭제
export const deleteChatRoom = async (chatRoomId: string, userId: string) => {
  const updates: { [key: string]: any } = {};

  // 채팅방 참조 가져오기
  const chatRoomRef = ref(database, `chatRooms/${chatRoomId}`);
  const chatRoomSnapshot = await get(chatRoomRef);

  if (chatRoomSnapshot.exists()) {
    const chatRoomData = chatRoomSnapshot.val();
    const participants = Object.keys(chatRoomData.participants);

    // 사용자의 채팅방 목록에서 삭제
    updates[`userChatRooms/${userId}/${chatRoomId}`] = null;

    // 다른 참여자의 채팅방 목록에서도 삭제 가능하나,
    // 일반적으로는 한 사람만 삭제하고 다른 사람은 유지하는 것이 일반적
    // 여기서는 사용자 자신의 목록에서만 삭제

    // 완전히 삭제하려면 다음과 같이 처리 (선택 사항)
    // participants.forEach(participantId => {
    //   updates[`userChatRooms/${participantId}/${chatRoomId}`] = null;
    // });
    // updates[`chatRooms/${chatRoomId}`] = null;
    // updates[`messages/${chatRoomId}`] = null;
  }

  // 업데이트 실행
  return update(ref(database), updates);
};

// 이메일로 회원가입
export const registerWithEmail = async (
  email: string,
  password: string,
  displayName: string
) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );

    // 사용자 프로필 업데이트
    await updateProfile(userCredential.user, {
      displayName: displayName,
    });

    return userCredential.user;
  } catch (error) {
    console.error("이메일 회원가입 오류:", error);
    throw error;
  }
};

// 이메일 중복 체크
export const checkEmailExists = async (email: string) => {
  try {
    // 이메일 소문자로 변환 (Firebase는 내부적으로 이메일을 소문자로 저장)
    const normalizedEmail = email.toLowerCase().trim();
    console.log(`이메일 중복 체크 시작: ${normalizedEmail}`);

    // 1. Firebase Auth 메서드를 사용하여 이메일 존재 여부 확인
    const methods = await fetchSignInMethodsForEmail(auth, normalizedEmail);
    console.log(`Auth 이메일 중복 체크 결과:`, methods);

    // Auth에서 이미 사용 중인 경우
    if (Array.isArray(methods) && methods.length > 0) {
      console.log(`Auth에서 이메일 중복 확인됨: ${normalizedEmail}`);
      return true;
    }

    // 2. 데이터베이스에서도 이메일 확인 (Auth와 DB 간의 불일치 방지)
    console.log(`데이터베이스에서 이메일 확인 중: ${normalizedEmail}`);
    const usersRef = ref(database, "users");
    const emailQuery = query(
      usersRef,
      orderByChild("email"),
      equalTo(normalizedEmail)
    );
    const snapshot = await get(emailQuery);

    const dbExists = snapshot.exists();
    console.log(`데이터베이스 이메일 중복 여부: ${dbExists}`);

    // 3. Firebase Auth에 등록된 사용자 목록에서 직접 확인 (추가 검증)
    // (이 부분은 일반적으로 관리자 SDK에서만 가능하지만,
    // 클라이언트 측에서는 이전 단계들로 충분히 검증 가능)

    // 최종 결과 (Auth 또는 DB 어느 하나라도 존재하면 true)
    const exists = (Array.isArray(methods) && methods.length > 0) || dbExists;
    console.log(`최종 이메일 중복 여부: ${exists}`);

    return exists;
  } catch (error) {
    console.error("이메일 중복 체크 오류:", error);

    // 에러 메시지를 콘솔에 상세히 출력
    if (error instanceof Error) {
      console.error("에러 상세 정보:", {
        message: error.message,
        name: error.name,
        stack: error.stack,
      });
    }

    // 오류 발생 시 안전하게 true 반환 (중복으로 처리)
    return true;
  }
};

// 전화번호 중복 체크
export const checkPhoneExists = async (phone: string) => {
  try {
    console.log("전화번호 중복 체크 시작:", phone);

    // 전화번호 형식 통일 (하이픈 제거)
    const cleanedPhone = phone.replace(/-/g, "");
    console.log("정규화된 전화번호:", cleanedPhone);

    // phone 필드로 인덱싱된 쿼리 사용
    const usersRef = ref(database, "users");
    const phoneQuery = query(
      usersRef,
      orderByChild("phone"),
      equalTo(cleanedPhone)
    );

    console.log("데이터베이스 쿼리 실행 중...");
    const snapshot = await get(phoneQuery);

    // 쿼리 결과 확인
    const exists = snapshot.exists();
    console.log("전화번호 중복 여부:", exists);

    return exists;
  } catch (error) {
    console.error("전화번호 중복 체크 오류:", error);

    // 에러 메시지를 콘솔에 상세히 출력
    if (error instanceof Error) {
      console.error("에러 상세 정보:", {
        message: error.message,
        name: error.name,
        stack: error.stack,
      });
    }

    // 오류 발생 시 기본적으로 false 반환 (안전을 위해)
    return false;
  }
};

// 이메일로 로그인
export const loginWithEmail = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    return userCredential.user;
  } catch (error) {
    console.error("이메일 로그인 오류:", error);
    throw error;
  }
};

// 로그아웃
export const logout = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("로그아웃 오류:", error);
    throw error;
  }
};

// 사용자 프로필 정보 저장
export const saveUserProfile = async (
  userId: string,
  name: string,
  phone: string,
  gender: string,
  birthdate: string,
  acceptNotifications: boolean
) => {
  try {
    console.log(`프로필 저장 시작 - 사용자 ID: ${userId}`);

    // 현재 인증 상태 확인
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error("프로필 저장 실패: 로그인 상태가 아닙니다");
      throw new Error("사용자가 로그인되어 있지 않습니다");
    }

    console.log(`현재 사용자: ${currentUser.uid}, 저장할 ID: ${userId}`);

    // 권한 사전 체크
    if (currentUser.uid !== userId) {
      console.error(
        `권한 오류: 현재 사용자(${currentUser.uid})와 프로필 저장 대상(${userId})이 일치하지 않습니다`
      );
      throw new Error("사용자 ID 불일치로 권한이 없습니다");
    }

    // 전화번호 형식 통일 (하이픈 제거)
    const cleanedPhone = phone.replace(/-/g, "");

    // 타임스탬프 생성
    const timestamp = new Date().toISOString();
    console.log(`프로필 저장 시간: ${timestamp}`);

    // 사용자 데이터 객체 생성
    const userData = {
      name,
      phone: cleanedPhone,
      gender,
      birthdate,
      acceptNotifications,
      email: currentUser.email,
      createdAt: timestamp,
      updatedAt: timestamp,
    };

    console.log("저장할 사용자 데이터:", userData);

    // 데이터베이스 참조 생성
    const userRef = ref(database, `users/${userId}`);

    // 데이터 저장
    await set(userRef, userData);
    console.log("사용자 프로필 저장 성공");

    return true;
  } catch (error: any) {
    console.error("사용자 프로필 저장 오류:", error);

    // 추가 디버깅 정보
    if (error.message && error.message.includes("PERMISSION_DENIED")) {
      console.error("Firebase 권한 오류: 데이터베이스 규칙을 확인하세요");
    }

    throw error;
  }
};

// 사용자 프로필 정보 가져오기
export const getUserProfile = async (userId: string) => {
  try {
    const userRef = ref(database, `users/${userId}`);
    const snapshot = await get(userRef);
    if (snapshot.exists()) {
      return snapshot.val();
    }
    return null;
  } catch (error) {
    console.error("사용자 프로필 가져오기 오류:", error);
    throw error;
  }
};

// Export
export { auth, database, db, storage };
export default app;

// 댓글 관련 함수들
export interface Comment {
  id: string;
  feedId: string;
  userId: string;
  userNickname: string;
  userProfileImage: string;
  content: string;
  createdAt: string;
  likes: number;
  likedUsers: { [userId: string]: boolean };
  parentCommentId?: string; // 답글의 경우 부모 댓글 ID
}

// 댓글 추가
export const addComment = async (
  feedId: string,
  userId: string,
  userNickname: string,
  userProfileImage: string,
  content: string,
  parentCommentId?: string
) => {
  const commentsRef = ref(database, `comments/${feedId}`);
  const newCommentRef = push(commentsRef);

  const commentData: Omit<Comment, "id"> = {
    feedId,
    userId,
    userNickname,
    userProfileImage,
    content,
    createdAt: new Date().toISOString(),
    likes: 0,
    likedUsers: {},
    ...(parentCommentId && { parentCommentId }),
  };

  await set(newCommentRef, commentData);

  // 피드의 댓글 수 업데이트
  const feedRef = ref(database, `feeds/${feedId}/commentCount`);
  const feedSnapshot = await get(feedRef);
  const currentCount = feedSnapshot.exists() ? feedSnapshot.val() : 0;
  await set(feedRef, currentCount + 1);

  return newCommentRef.key;
};

// 특정 피드의 댓글 목록 가져오기
export const getComments = async (feedId: string): Promise<Comment[]> => {
  const commentsRef = ref(database, `comments/${feedId}`);
  const snapshot = await get(commentsRef);

  if (!snapshot.exists()) {
    return [];
  }

  const comments: Comment[] = [];
  snapshot.forEach((childSnapshot) => {
    const comment = childSnapshot.val();
    comments.push({
      id: childSnapshot.key!,
      feedId: comment.feedId || feedId,
      userId: comment.userId || "",
      userNickname: comment.userNickname || "익명",
      userProfileImage:
        comment.userProfileImage || "/images/icons/default-profile.png",
      content: comment.content || "",
      createdAt: comment.createdAt || new Date().toISOString(),
      likes: comment.likes || 0,
      likedUsers: comment.likedUsers || {},
      ...(comment.parentCommentId && {
        parentCommentId: comment.parentCommentId,
      }),
    });
  });

  // 생성 시간순으로 정렬 (최신이 위로)
  comments.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return comments;
};

// 댓글 실시간 리스너
export const listenToComments = (
  feedId: string,
  callback: (comments: Comment[]) => void
) => {
  const commentsRef = ref(database, `comments/${feedId}`);

  return onValue(commentsRef, (snapshot) => {
    const comments: Comment[] = [];

    if (snapshot.exists()) {
      snapshot.forEach((childSnapshot) => {
        const comment = childSnapshot.val();
        comments.push({
          id: childSnapshot.key!,
          feedId: comment.feedId || feedId,
          userId: comment.userId || "",
          userNickname: comment.userNickname || "익명",
          userProfileImage:
            comment.userProfileImage || "/images/icons/default-profile.png",
          content: comment.content || "",
          createdAt: comment.createdAt || new Date().toISOString(),
          likes: comment.likes || 0,
          likedUsers: comment.likedUsers || {},
          ...(comment.parentCommentId && {
            parentCommentId: comment.parentCommentId,
          }),
        });
      });

      // 생성 시간순으로 정렬 (최신이 위로)
      comments.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    }

    callback(comments);
  });
};

// 댓글 좋아요/좋아요 취소
export const toggleCommentLike = async (
  feedId: string,
  commentId: string,
  userId: string
) => {
  const commentRef = ref(database, `comments/${feedId}/${commentId}`);
  const snapshot = await get(commentRef);

  if (!snapshot.exists()) {
    throw new Error("댓글을 찾을 수 없습니다.");
  }

  const comment = snapshot.val();
  const likedUsers = comment.likedUsers || {};
  const isLiked = likedUsers[userId] === true;

  const updates: { [key: string]: any } = {};

  if (isLiked) {
    // 좋아요 취소
    updates[`comments/${feedId}/${commentId}/likedUsers/${userId}`] = null;
    updates[`comments/${feedId}/${commentId}/likes`] = Math.max(
      0,
      (comment.likes || 0) - 1
    );
  } else {
    // 좋아요 추가
    updates[`comments/${feedId}/${commentId}/likedUsers/${userId}`] = true;
    updates[`comments/${feedId}/${commentId}/likes`] = (comment.likes || 0) + 1;
  }

  await update(ref(database), updates);
  return !isLiked;
};

// 댓글 삭제
export const deleteComment = async (feedId: string, commentId: string) => {
  const commentRef = ref(database, `comments/${feedId}/${commentId}`);

  // 댓글이 존재하는지 확인
  const snapshot = await get(commentRef);
  if (!snapshot.exists()) {
    throw new Error("댓글을 찾을 수 없습니다.");
  }

  // 댓글 삭제
  await remove(commentRef);

  // 피드의 댓글 수 업데이트
  const feedRef = ref(database, `feeds/${feedId}/commentCount`);
  const feedSnapshot = await get(feedRef);
  const currentCount = feedSnapshot.exists() ? feedSnapshot.val() : 0;
  await set(feedRef, Math.max(0, currentCount - 1));
};

// 댓글 수정
export const updateComment = async (
  feedId: string,
  commentId: string,
  newContent: string
) => {
  const commentRef = ref(database, `comments/${feedId}/${commentId}/content`);
  await set(commentRef, newContent);

  // 수정 시간도 업데이트
  const updatedAtRef = ref(
    database,
    `comments/${feedId}/${commentId}/updatedAt`
  );
  await set(updatedAtRef, new Date().toISOString());
};

// 피드 좋아요/좋아요 취소
export const toggleFeedLike = async (feedId: string, userId: string) => {
  const feedRef = ref(database, `feeds/${feedId}`);
  const snapshot = await get(feedRef);

  if (!snapshot.exists()) {
    throw new Error("피드를 찾을 수 없습니다.");
  }

  const feed = snapshot.val();
  const likedUsers = feed.likedUsers || {};
  const isLiked = likedUsers[userId] === true;

  const updates: { [key: string]: any } = {};

  if (isLiked) {
    // 좋아요 취소
    updates[`feeds/${feedId}/likedUsers/${userId}`] = null;
    updates[`feeds/${feedId}/likeCount`] = Math.max(
      0,
      (feed.likeCount || 0) - 1
    );
  } else {
    // 좋아요 추가
    updates[`feeds/${feedId}/likedUsers/${userId}`] = true;
    updates[`feeds/${feedId}/likeCount`] = (feed.likeCount || 0) + 1;
  }

  await update(ref(database), updates);
  return !isLiked;
};

// 피드 좋아요 수 가져오기
export const getFeedLikeInfo = async (feedId: string, userId?: string) => {
  const feedRef = ref(database, `feeds/${feedId}`);
  const snapshot = await get(feedRef);

  if (!snapshot.exists()) {
    return { likeCount: 0, isLiked: false };
  }

  const feed = snapshot.val();
  const likeCount = feed.likeCount || 0;
  const isLiked =
    userId && feed.likedUsers ? feed.likedUsers[userId] === true : false;

  return { likeCount, isLiked };
};
